import { ISvcChipGroupedOption, ISvcChipOption } from '@solvace/component/svc-controls';

/**
 * Prefixes used to disambiguate user vs team selections inside a single
 * `FormControl` value. The "Atores" field accepts both kinds of owners, so
 * every option's `value` is encoded as `${OWNER_USER_PREFIX}${userId}` or
 * `${OWNER_TEAM_PREFIX}${teamId}` and decoded back on submit/hydration.
 *
 * Keep the prefixes short and unique so the decoder can split them cleanly
 * with a single `startsWith` check.
 */
export const OWNER_USER_PREFIX = 'user:';
export const OWNER_TEAM_PREFIX = 'team:';

/** Minimal shape of a user option (already present in the modal combos). */
export interface OwnerUserSource {
  value: string;
  label: string;
}

/** Minimal shape of a team option (mapped from `UserService.getTeamsInfo`). */
export interface OwnerTeamSource {
  value: number;
  label: string;
}

/** Group labels used by the grouped chip. The caller passes them already
 *  translated (Transloco lives in the component layer, this module stays
 *  framework-free so it can be unit-tested in isolation). */
export interface OwnerGroupLabels {
  users: string;
  teams: string;
}

export interface OwnerSelection {
  /** GUIDs of the picked users. Empty array when nothing is picked. */
  userOwners: string[];
  /** Numeric ids of the picked teams. Empty array when nothing is picked. */
  teamOwners: number[];
}

/**
 * Builds the grouped option list the `svc-chip-grouped` control renders. Two
 * groups are produced — Users first, Teams second — and the values are
 * prefixed so the decoder can split them later.
 *
 * Returning an `ISvcChipGroupedOption[]` keeps the shape compatible with the
 * existing grouped-chip pattern used elsewhere in the project (e.g. systems
 * picker in groups-registration).
 */
export function buildOwnerOptions(
  users: ReadonlyArray<OwnerUserSource> | null | undefined,
  teams: ReadonlyArray<OwnerTeamSource> | null | undefined,
  labels: OwnerGroupLabels,
): ISvcChipGroupedOption[] {
  const groups: ISvcChipGroupedOption[] = [];

  const userOptions = normaliseUserOptions(users);
  if (userOptions.length) {
    groups.push({ group: labels.users, options: userOptions });
  }

  const teamOptions = normaliseTeamOptions(teams);
  if (teamOptions.length) {
    groups.push({ group: labels.teams, options: teamOptions });
  }

  return groups;
}

/**
 * Splits the prefixed `FormControl` value back into the two parallel arrays
 * the backend expects on the create / update payload. Unknown / malformed
 * entries are silently dropped (defensive — easier to debug a missing entry
 * than a crash on submit).
 */
export function decodeOwnerSelection(values: ReadonlyArray<string | number | null | undefined> | null | undefined): OwnerSelection {
  const userOwners: string[] = [];
  const teamOwners: number[] = [];
  const seenUsers = new Set<string>();
  const seenTeams = new Set<number>();

  for (const entry of values ?? []) {
    const raw = String(entry ?? '').trim();
    if (!raw) continue;

    if (raw.startsWith(OWNER_USER_PREFIX)) {
      const userId = raw.slice(OWNER_USER_PREFIX.length).trim();
      if (userId && !seenUsers.has(userId)) {
        seenUsers.add(userId);
        userOwners.push(userId);
      }
      continue;
    }

    if (raw.startsWith(OWNER_TEAM_PREFIX)) {
      const rawId = raw.slice(OWNER_TEAM_PREFIX.length).trim();
      if (!rawId) continue;
      const teamId = Number(rawId);
      if (Number.isFinite(teamId) && teamId !== 0 && !seenTeams.has(teamId)) {
        seenTeams.add(teamId);
        teamOwners.push(teamId);
      }
    }
  }

  return { userOwners, teamOwners };
}

/**
 * Inverse of `decodeOwnerSelection`: takes the parallel arrays the API
 * returns on `GET /ActionPlan/{id}` and produces the single prefixed array
 * the chip-grouped control binds to.
 */
export function encodeOwnerSelection(
  userOwners: ReadonlyArray<string | null | undefined> | null | undefined,
  teamOwners: ReadonlyArray<number | string | null | undefined> | null | undefined,
): string[] {
  const result: string[] = [];
  const seen = new Set<string>();

  for (const userId of userOwners ?? []) {
    const trimmed = String(userId ?? '').trim();
    if (!trimmed) continue;
    const encoded = `${OWNER_USER_PREFIX}${trimmed}`;
    if (seen.has(encoded)) continue;
    seen.add(encoded);
    result.push(encoded);
  }

  for (const teamId of teamOwners ?? []) {
    const numericId = Number(teamId);
    if (!Number.isFinite(numericId)) continue;
    const encoded = `${OWNER_TEAM_PREFIX}${numericId}`;
    if (seen.has(encoded)) continue;
    seen.add(encoded);
    result.push(encoded);
  }

  return result;
}

/**
 * True when the encoded value belongs to the "teams" group. Used by the
 * read-only / template branches to decide whether to render a smart-user
 * avatar (users) or a team icon.
 */
export function isTeamOwnerValue(value: string | number | null | undefined): boolean {
  return String(value ?? '').startsWith(OWNER_TEAM_PREFIX);
}

/**
 * Strips the `user:` / `team:` prefix from an encoded value. Returns the
 * empty string when the value is missing or malformed.
 */
export function stripOwnerPrefix(value: string | number | null | undefined): string {
  const raw = String(value ?? '').trim();
  if (raw.startsWith(OWNER_USER_PREFIX)) return raw.slice(OWNER_USER_PREFIX.length);
  if (raw.startsWith(OWNER_TEAM_PREFIX)) return raw.slice(OWNER_TEAM_PREFIX.length);
  return '';
}

// ─── private ───────────────────────────────────────────────────────────────

function normaliseUserOptions(users: ReadonlyArray<OwnerUserSource> | null | undefined): ISvcChipOption[] {
  if (!Array.isArray(users)) return [];
  const result: ISvcChipOption[] = [];
  const seen = new Set<string>();
  for (const user of users) {
    const id = String(user?.value ?? '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    result.push({ value: `${OWNER_USER_PREFIX}${id}`, label: user?.label ?? '' });
  }
  return result;
}

function normaliseTeamOptions(teams: ReadonlyArray<OwnerTeamSource> | null | undefined): ISvcChipOption[] {
  if (!Array.isArray(teams)) return [];
  const result: ISvcChipOption[] = [];
  const seen = new Set<number>();
  for (const team of teams) {
    const id = Number(team?.value);
    if (!Number.isFinite(id) || seen.has(id)) continue;
    seen.add(id);
    result.push({ value: `${OWNER_TEAM_PREFIX}${id}`, label: team?.label ?? '' });
  }
  return result;
}
